import { DecorItemI, TriangularPrismData } from "../shared";

/**
 * Функция для создания треугольной призмы из прямоугольного перекрытия
 * путем отсечения половины по диагонали
 */

// Используем импортированный интерфейс вместо локального
type TriangularPrismResult = TriangularPrismData;

/**
 * Создает треугольную призму из прямоугольного перекрытия
 * @param decor - декоративный элемент (перекрытие)
 * @param direction - направление отсечения треугольника
 * @returns обновленный декор с параметрами треугольной призмы
 */
export function createTriangularPrism(
    decor: DecorItemI,
    direction: "top-left" | "top-right" | "bottom-left" | "bottom-right" = "top-right"
): DecorItemI {
    // console.log("🔺 ВХОД В createTriangularPrism");
    // console.log("📦 Декор:", decor.name, "Surface:", decor.surface);
    // console.log("📐 Направление отсечения (в плоскости ZY - вид сбоку):", direction);
    // console.log("📦 Текущие размеры:", {
    //     width: `${(decor.width * 1000).toFixed(0)} мм`,
    //     height: `${(decor.height * 1000).toFixed(0)} мм`,
    //     depth: `${(decor.depth * 1000).toFixed(0)} мм`
    // });

    // Получаем размеры декора, учитывая что перекрытия могут быть не инициализированы
    let { width, height, depth, position } = decor;

    // Если декор не инициализирован (размеры = 0), используем стандартные размеры перекрытия
    if (width === 0 || height === 0 || depth === 0) {
        // console.log("⚠️ Декор не инициализирован, используем стандартные размеры перекрытия");

        // Логика из ConstructionsPrimitive для размеров перекрытий
        width = 1; // 1 метр ширина
        height = 0.2; // 20 см высота (толщина перекрытия)
        depth = 1; // 1 метр глубина

        // console.log("📦 Использованы размеры:", {
        //     width: `${(width * 1000).toFixed(0)} мм`,
        //     height: `${(height * 1000).toFixed(0)} мм`,
        //     depth: `${(depth * 1000).toFixed(0)} мм`
        // });
    }

    const [x, y, z] = position;

    // Вычисляем координаты углов прямоугольного перекрытия
    const halfWidth = width / 2;
    const halfDepth = depth / 2;

    // Координаты углов в локальной системе координат (относительно центра)
    const corners = {
        topLeft: [-halfWidth, 0, -halfDepth],
        topRight: [halfWidth, 0, -halfDepth],
        bottomLeft: [-halfWidth, 0, halfDepth],
        bottomRight: [halfWidth, 0, halfDepth],
    };

    // Определяем плоскость отсечения в зависимости от направления
    let cutPlane: TriangularPrismResult["cutPlane"];

    switch (direction) {
        case "top-right":
            // Отсекаем правый верхний треугольник (диагональ от top-left к bottom-right)
            cutPlane = {
                point1: [x + corners.topLeft[0], y, z + corners.topLeft[2]],
                point2: [x + corners.topLeft[0], y + height, z + corners.topLeft[2]],
                point3: [x + corners.bottomRight[0], y + height, z + corners.bottomRight[2]],
                point4: [x + corners.bottomRight[0], y, z + corners.bottomRight[2]],
            };
            break;
        case "top-left":
            // Отсекаем левый верхний треугольник (диагональ от top-right к bottom-left)
            cutPlane = {
                point1: [x + corners.topRight[0], y, z + corners.topRight[2]],
                point2: [x + corners.topRight[0], y + height, z + corners.topRight[2]],
                point3: [x + corners.bottomLeft[0], y + height, z + corners.bottomLeft[2]],
                point4: [x + corners.bottomLeft[0], y, z + corners.bottomLeft[2]],
            };
            break;
        case "bottom-right":
            // Отсекаем правый нижний треугольник (диагональ от bottom-left к top-right)
            cutPlane = {
                point1: [x + corners.bottomLeft[0], y, z + corners.bottomLeft[2]],
                point2: [x + corners.bottomLeft[0], y + height, z + corners.bottomLeft[2]],
                point3: [x + corners.topRight[0], y + height, z + corners.topRight[2]],
                point4: [x + corners.topRight[0], y, z + corners.topRight[2]],
            };
            break;
        case "bottom-left":
            // Отсекаем левый нижний треугольник (диагональ от top-left к bottom-right, но наоборот)
            cutPlane = {
                point1: [x + corners.topLeft[0], y, z + corners.topLeft[2]],
                point2: [x + corners.topLeft[0], y + height, z + corners.topLeft[2]],
                point3: [x + corners.bottomRight[0], y + height, z + corners.bottomRight[2]],
                point4: [x + corners.bottomRight[0], y, z + corners.bottomRight[2]],
            };
            break;
    }

    const triangularPrism: TriangularPrismResult = {
        isTriangular: true,
        triangleDirection: direction,
        cutPlane,
    };

    // console.log("✂️ Плоскость отсечения создана:");
    // console.log("   Точка 1:", cutPlane?.point1?.map(coord => `${(coord * 1000).toFixed(0)}мм`));
    // console.log("   Точка 2:", cutPlane?.point2?.map(coord => `${(coord * 1000).toFixed(0)}мм`));
    // console.log("   Точка 3:", cutPlane?.point3?.map(coord => `${(coord * 1000).toFixed(0)}мм`));
    // console.log("   Точка 4:", cutPlane?.point4?.map(coord => `${(coord * 1000).toFixed(0)}мм`));

    // Создаем обновленный декор с добавленными свойствами треугольной призмы
    const updatedDecor = {
        ...decor,
        width, // Обновляем размеры, если они были исправлены
        height,
        depth,
        triangularPrism,
    };

    // console.log("✅ Треугольная призма создана успешно");

    return updatedDecor;
}

/**
 * Сбрасывает треугольную призму обратно в прямоугольное перекрытие
 */
export function resetTriangularPrism(decor: DecorItemI): DecorItemI {
    // console.log("🔄 Сброс треугольной призмы для:", decor.name);

    const { triangularPrism, ...resetDecor } = decor;

    // console.log("✅ Треугольная призма сброшена, возвращен прямоугольный декор");

    return resetDecor;
}

/**
 * Проверяет, является ли декор треугольной призмой
 */
export function isTriangularPrism(decor: DecorItemI): boolean {
    const result = !!decor.triangularPrism?.isTriangular;
    // console.log("🔍 isTriangularPrism check:", {
    //     hasTriangularPrism: !!decor.triangularPrism,
    //     isTriangular: decor.triangularPrism?.isTriangular,
    //     result
    // });
    return result;
}

/**
 * Циклически переключает направление треугольной призмы
 */
export function cycleTriangularDirection(
    currentDirection: TriangularPrismResult["triangleDirection"]
): TriangularPrismResult["triangleDirection"] {
    const directions: Array<TriangularPrismResult["triangleDirection"]> = [
        "top-right",
        "bottom-right",
        "bottom-left",
        "top-left",
    ];

    const currentIndex = directions.indexOf(currentDirection || "top-right");
    const nextIndex = (currentIndex + 1) % directions.length;

    return directions[nextIndex];
}
